import {
  Box,
  Button,
  Card,
  Flex,
  IconLoading,
  LegacyCheckbox,
  LegacySwitch,
  Table,
  Text,
  Tooltip,
} from "@powerledger/ui-component-lib";
import currency from "currency.js";
import { format } from "date-fns";
import { startCase, toLower } from "lodash";
import React, { FC, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CellProps, Column, Row } from "react-table";

import AnonymizeTooltip from "@/app/components/anonymize-tooltip";
import { EligibilityValueFormatter } from "@/app/components/eligibility-value-formatter";
import { ErrorBoundary } from "@/app/components/error-boundary";
import { LocationValueWrapper } from "@/app/components/location-value-wrapper/location-value-wrapper";
import { OrderCancelModal } from "@/app/components/order-cancel-modal";
import { PageHeader } from "@/app/components/page-header/page-header";
import { RefreshIconButton } from "@/app/components/refresh-icon-button";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { decodeAndParseHtmlContent } from "@/app/lib/html-encoder-decoder";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { CertificationAndEliglibilityType } from "../../lib/format-attributes";
import { getFuelSourceIcon } from "../../lib/get-fuel-source-icon";
import { getTableTranslation } from "../../lib/get-translations-for-components";
import { OrderPosition, RegistryCode } from "../../types/generated/graphql";
import { OrderHistoryProps, OrderHistoryTableData } from "./order-history.types";

const TransactionSummaryDownloadButton = React.lazy(
  () =>
    import(
      "../../components/download-report-components/transaction-summary-download-button/transaction-summary-download-button"
    ),
);

export const OrderHistoryView: FC<OrderHistoryProps> = ({
  tableData,
  loading,
  reloadOrderHistory,
  cancelOrderId,
  getOrderIsCancellable,
  initOrderCancellation,
  showOrderCancelModal,
  setShowOrderCancelModal,
  getCanDownloadTransactionSummary,
  handleFilterChange,
  typeFilter,
  pageInfo,
  fetchData,
  typeFilterChanged,
  recOptions,
  showAllAttributes,
  setShowAllAttributes,
  registryCode,
  orderHiddenColumn,
}) => {
  const { t } = useTranslation();
  const isActiveRegistryErcot = registryCode === RegistryCode.Ercot;

  const tableColumns = useMemo(() => {
    const columns: Column<OrderHistoryTableData>[] = [
      {
        Header: t("Order Number"),
        accessor: "orderNumber",
        disableSortBy: true,
        Cell: ({ value }) => <TableValueWrapper value={value} containerSx={{ minWidth: 90 }} />,
      },
      {
        Header: t("Vintage"),
        disableSortBy: true,
        accessor: (row) => row.vintages,
        Cell: ({ value }: { value: string }) => {
          return <TableValueWrapper value={value} csv title="Vintages" containerSx={{ minWidth: 80 }} />;
        },
      },
      {
        Header: t(isActiveRegistryErcot ? "Certification" : "Eligibility"),
        accessor: "certificationsAndEligibilities",
        disableSortBy: true,
        Cell: ({ value, row }: { row: Row<OrderHistoryTableData>; value: CertificationAndEliglibilityType[] }) => {
          return (
            <EligibilityValueFormatter
              certificationsAndEligibilities={value}
              position={row.original.position}
              title={isActiveRegistryErcot ? "Certifications" : "Eligibilities"}
            />
          );
        },
      },
      {
        Header: t(isActiveRegistryErcot ? "FIN" : "Project"),
        accessor: "projects",
        disableSortBy: true,
        Cell: ({ row, value }: { row: Row<OrderHistoryTableData>; value: string | string[] }) => {
          const { projectVisibility, position } = row.original;
          const projectValue = value || value?.length ? value : typeFilter === OrderPosition.Bid ? "Any" : "-";
          const showTooltip = position === OrderPosition.Ask && !projectVisibility;
          return (
            <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <TableValueWrapper value={projectValue} csv title={"Projects"} containerSx={{ minWidth: 100 }} />
              {showTooltip && <AnonymizeTooltip tooltipId={row.original.id} />}
            </Flex>
          );
        },
      },

      {
        Header: t(isActiveRegistryErcot ? "Technology Type" : "Fuel Type"),
        accessor: (row) => row.attributes.fuelSources,
        Cell: ({ value }: { value: string[] }) => {
          return (
            <Flex sx={{ color: "textDark" }}>
              {!value || value.length < 1 ? (
                typeFilter === OrderPosition.Bid ? (
                  "Any"
                ) : (
                  "-"
                )
              ) : (
                <TableValueWrapper
                  title={isActiveRegistryErcot ? "Technology Types" : "Fuel Types"}
                  getIcon={getFuelSourceIcon}
                  value={value}
                  containerSx={{ minWidth: 110 }}
                  getToolTipText={(key: string) =>
                    recOptions?.fuelSourceOptions?.filter((x) => x.value === key)[0]?.label || ""
                  }
                />
              )}
            </Flex>
          );
        },
        disableSortBy: true,
      },
      {
        Header: t("Certificate Type"),
        id: "certificateTypes",
        accessor: (row) => row.certificateTypes,
        disableSortBy: true,
        Cell: ({ value }: { value: string[] }) => (
          <Flex sx={{ color: "textDark" }}>
            {value?.length === 0 ? "Any" : <TableValueWrapper value={value} csv title="Certificate Types" />}
          </Flex>
        ),
      },
      {
        Header: t("Commencement Date (COD)"),
        id: "commencementDate",
        Cell: ({ row }: { row: Row<OrderHistoryTableData> }) => {
          const commencementDate = row.original.attributes?.commencementDate;
          const displayValue = commencementDate ? format(new Date(commencementDate), "P") : "N/A";
          return <TableValueWrapper value={displayValue} />;
        },
        disableSortBy: true,
      },
      {
        Header: t("Status"),
        accessor: "status",
        Cell: ({ value, row: { original } }) => (
          <Flex sx={{ color: "textDark", alignItems: "center" }}>
            {`${t(startCase(toLower(value)))}`}
            {original.statusMessage && (
              <Tooltip
                id={`order-${original.id}-status-tooltip`}
                content={<Text>{decodeAndParseHtmlContent(t(original.statusMessage))}</Text>}
                sx={{
                  svg: {
                    width: 16,
                    height: 16,
                    path: {
                      fill: "primary.500",
                    },
                  },
                }}
              />
            )}
          </Flex>
        ),
      },
      {
        Header: (
          <Box
            sx={{
              margin: "auto",
            }}
          >
            {t("Total")}
          </Box>
        ),
        id: "total",
        accessor: (row) =>
          currency(row.price?.amount || 0, { fromCents: true })
            .multiply(row.initialVolume)
            .format(),
        width: "100px",
        Cell: ({ value }: { value: string }) => (
          <Box
            sx={{
              textAlign: "right",
              width: "100px",
              px: 2,
            }}
          >
            {value}
          </Box>
        ),
      },
      {
        Header: t("Sale Timestamp"),
        accessor: "completedOn",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TableValueWrapper value={value ? format(new Date(value), "p") + " " + format(new Date(value), "P") : "-"} />
        ),
      },
      {
        Header: t("Expiry Date"),
        accessor: "expiryDate",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TableValueWrapper value={formatDate(value, { formatStyle: AppDateFormats.UniversalDateTimeFormat })} />
        ),
      },
      {
        Header: (
          <Box
            sx={{
              margin: "auto",
            }}
          >
            {t("Unit Price")}
          </Box>
        ),
        id: "unitPrice",

        disableSortBy: true,
        width: "100px",
        Cell: ({ row }: { row: Row<OrderHistoryTableData> }) => (
          <Box
            sx={{
              textAlign: "right",
              width: "100px",
              px: 2,
            }}
          >
            {currency(row.original?.price?.amount || 0, { fromCents: true }).format()}
          </Box>
        ),
      },
      {
        Header: t("Fulfilled Quantity"),
        id: "fulfilledVolume",
        disableSortBy: true,
        Cell: ({ row }: { row: Row<OrderHistoryTableData> }) => (
          <TableValueWrapper
            value={currency(
              row.original.initialVolume - (row.original.currentVolume + (row.original.cancelledVolume ?? 0)),
              {
                symbol: "",
                separator: ",",
                precision: 0,
              },
            ).format()}
          />
        ),
      },
      {
        Header: t("Withdrawn Quantity"),
        accessor: "cancelledVolume",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TableValueWrapper
            value={currency(value ?? 0, {
              symbol: "",
              separator: ",",
              precision: 0,
            }).format()}
          />
        ),
      },
      {
        Header: t("Ordered Quantity"),
        accessor: "initialVolume",
        disableSortBy: true,
        Cell: ({ value }) => (
          <TableValueWrapper
            value={currency(value ?? 0, {
              symbol: "",
              separator: ",",
              precision: 0,
            }).format()}
          />
        ),
      },
      {
        Header: t("Order Type"),
        accessor: "type",
        disableSortBy: true,
        Cell: ({ value }) => <TableValueWrapper value={value} />,
      },
      {
        Header: t("Location"),
        accessor: "locations",
        disableSortBy: true,
        Cell: ({ value }) => <LocationValueWrapper value={value ?? []} csv containerSx={{ minWidth: 110 }} />,
      },
    ];
    columns.push({
      Header: t("Action"),
      id: "action",
      Cell: ({ row: { original } }: CellProps<OrderHistoryTableData>) => (
        <Flex sx={{ justifyContent: "center", alignItems: "center", width: 110 }}>
          {getOrderIsCancellable(original) && (
            <Button
              variant="pill.compactSecondary"
              sx={{ py: 1, mr: 2 }}
              onClick={() => initOrderCancellation(original.id)}
              data-testid="withdraw-order"
            >
              {t("Withdraw")}
            </Button>
          )}
          {getCanDownloadTransactionSummary(original) && (
            <ErrorBoundary fallback={<div>{t("Something went wrong")}</div>}>
              <Suspense fallback={<IconLoading sx={{ ml: 2 }} size="small" />}>
                <TransactionSummaryDownloadButton order={original} isActiveRegistryErcot={isActiveRegistryErcot} />
              </Suspense>
            </ErrorBoundary>
          )}
        </Flex>
      ),
    });
    return columns;
  }, [
    t,
    isActiveRegistryErcot,
    typeFilter,
    recOptions?.fuelSourceOptions,
    getOrderIsCancellable,
    getCanDownloadTransactionSummary,
    initOrderCancellation,
  ]);

  return (
    <Box as="main" sx={{ m: 4 }}>
      <PageHeader
        title={t("Order History")}
        filterProps={{
          applyFilters: handleFilterChange,
          shouldSelectVintage: false,
          hideStatusFilter: false,
          hideLocationFilter: registryCode === RegistryCode.Ercot,
          hideCertificateTypeFilter: registryCode === RegistryCode.Mrets,
        }}
        activeRegistryCode={registryCode as TraceXSupportedRegistries}
      />

      <Flex sx={{ alignItems: "center", mb: 2 }}>
        <Flex sx={{ ml: "auto" }}>
          <RefreshIconButton loading={loading} fetchData={reloadOrderHistory} />
        </Flex>
      </Flex>

      <Card variant="compact">
        <Flex
          sx={{
            background: "lightActive",
            p: 3,
            pb: 3,
            mb: -1,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            justifyContent: "space-between",
            borderWidth: "0.5px",
            borderStyle: "solid",
            borderColor: "shadow",
            zIndex: 1,
            position: "relative",
            borderBottomWidth: 0,
          }}
        >
          <LegacyCheckbox
            checked={showAllAttributes}
            sx={{
              color: "textDarker",
              fontWeight: "bold",
            }}
            label={t("Show All Attributes")}
            onChange={() => setShowAllAttributes((prev) => !prev)}
            id="show-all-attributes"
          />
          <LegacySwitch
            checked={typeFilter === OrderPosition.Ask}
            uncheckedLabel={t("Bought")}
            checkedLabel={t("Sold")}
            onChange={typeFilterChanged}
          />
        </Flex>
        <Box
          sx={{
            table: {
              td: {
                whiteSpace: "unset",
              },
            },
          }}
        >
          <Table
            columns={tableColumns}
            dataSource={tableData}
            pageInfo={pageInfo}
            initialState={{ hiddenColumns: orderHiddenColumn }}
            fetchData={fetchData}
            manualPagination
            loading={loading}
            showScrollArrows
            translation={getTableTranslation(t)}
            containerSx={{
              table: {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderTop: 0,
              },
            }}
            testId="order-history-spot-table"
          />
        </Box>
      </Card>
      <OrderCancelModal
        visible={showOrderCancelModal}
        orderId={cancelOrderId}
        closeModal={() => setShowOrderCancelModal(false)}
      />
    </Box>
  );
};
